import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import axios from 'axios';

import Header from '../components/Header';
import { Footer } from '../components/Footer';
import PaymentMethodSwitch, { type PaymentMethod } from '../components/PaymentMethodSwitch';
import TurnstileWidget from '../components/TurnstileWidget';
import { SKIP_TURNSTILE } from '../config/features';
import { CompraAvulsaService, type CompraAvulsa, type ProdutoAvulso } from '../services/compra-avulsa.service';
import { applyMask, getMaskProps } from '../utils/masks';
import BuilderLivro from './livro-customizado/BuilderLivro';
import {
  limparRascunhoSalvo,
  MSG_RASCUNHO_VENCIDO,
  rascunhoVencido,
  type LivroPronto,
} from './livro-customizado/rascunho';
import { isInApp } from '../utils/inApp';

const formatPreco = (centavos: number) => `R$ ${(centavos / 100).toFixed(2).replace('.', ',')}`;

const POLLING_PIX_MS = 5000;

/** QR do PIX: imagem da AppMax quando vem; senão gera a partir do copia-e-cola. */
function urlQrCode(pix: NonNullable<CompraAvulsa['pix']>): string {
  const qr = pix.qrCode ?? '';
  if (qr.startsWith('data:image') || qr.startsWith('http')) return qr;
  if (/^[A-Za-z0-9+/]+=*$/.test(qr) && qr.length > 100) return `data:image/png;base64,${qr}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pix.emv)}`;
}

/** `reason` da API que manda de volta pro builder (livro incompleto/expirado/já pago). */
function reasonDoErro(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const r = (err.response?.data as { reason?: unknown } | undefined)?.reason;
  return typeof r === 'string' ? r : undefined;
}

function mensagemDeErro(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (!err.response) return 'Sem resposta do servidor. Confira sua conexão e tente de novo.';
    const msg = (err.response.data as { message?: unknown } | undefined)?.message;
    if (typeof msg === 'string' && msg) return msg;
    if (Array.isArray(msg) && msg.length) return 'Confira os dados preenchidos.';
  }
  return 'Não foi possível concluir o pagamento. Tente de novo.';
}

const novaChave = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

interface Props {
  produto: string;
}

/**
 * Checkout de compra avulsa pra quem já é assinante (1º produto: Livro
 * customizado). Sem assinatura nova: o assinante se identifica pelo e-mail de
 * acesso e paga 1x no cartão ou PIX. Tudo o mais (nome, CPF, endereço) o backend
 * lê da assinatura.
 */
export default function CompraAvulsaPage({ produto: slug }: Props) {
  const inApp = isInApp();
  const [produto, setProduto] = useState<ProdutoAvulso | null>(null);
  const [erroCarga, setErroCarga] = useState(false);

  const [email, setEmail] = useState('');
  const [metodo, setMetodo] = useState<PaymentMethod>('credit-card');
  const [nomeCartao, setNomeCartao] = useState('');
  const [numero, setNumero] = useState('');
  const [validade, setValidade] = useState('');
  const [cvv, setCvv] = useState('');

  const [turnstileToken, setTurnstileToken] = useState('');
  // Token do Turnstile é de uso único: cada tentativa remonta o widget.
  const [turnstileKey, setTurnstileKey] = useState(0);
  const requireTurnstile = !SKIP_TURNSTILE;

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [compra, setCompra] = useState<CompraAvulsa | null>(null);
  // Livro customizado: montado no builder antes do formulário de pagamento.
  const [livro, setLivro] = useState<LivroPronto | null>(null);
  // Mensagem pro builder quando o pagamento devolve pra ele (ex.: rascunho vencido).
  const [avisoLivro, setAvisoLivro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  // Mesma chave enquanto a tentativa não tem resposta (clique duplo/timeout
  // devolvem a mesma compra); troca depois de uma resposta definitiva.
  const chaveRef = useRef<string>(novaChave());

  useEffect(() => {
    CompraAvulsaService.produto(slug)
      .then(setProduto)
      .catch(() => setErroCarga(true));
  }, [slug]);

  // Polling enquanto o PIX (ou cartão em análise) não se resolve.
  useEffect(() => {
    if (!compra || compra.status !== 'pendente') return;
    const id = window.setInterval(() => {
      CompraAvulsaService.status(compra.compraId)
        .then((atual) => {
          if (atual.status === 'recusada') {
            // Cartão que estava em análise e foi recusado: volta pro formulário.
            setErro('Pagamento recusado pela operadora do cartão. Confira os dados ou tente outro cartão.');
            setCompra(null);
            return;
          }
          setCompra((anterior) => (anterior?.compraId === atual.compraId ? atual : anterior));
        })
        .catch(() => undefined);
    }, POLLING_PIX_MS);
    return () => window.clearInterval(id);
  }, [compra]);

  const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const [mes, ano] = validade.split('/');
  const cartaoValido =
    nomeCartao.trim().length >= 3 &&
    // O tokenize da AppMax só aceita cartão de 16 dígitos.
    numero.replace(/\D/g, '').length === 16 &&
    /^(0[1-9]|1[0-2])$/.test(mes ?? '') &&
    /^\d{4}$/.test(ano ?? '') &&
    /^\d{3,4}$/.test(cvv);
  const precisaLivro = !!produto?.exigeLivroHeroBook;
  const podeEnviar =
    !!produto &&
    (!precisaLivro || !!livro) &&
    emailValido &&
    (metodo === 'pix' || cartaoValido) &&
    (!requireTurnstile || !!turnstileToken) &&
    !enviando;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!podeEnviar || !produto) return;
    setEnviando(true);
    setErro(null);
    const base = {
      produto: produto.slug,
      email: email.trim(),
      idempotencyKey: chaveRef.current,
      turnstileToken: turnstileToken || undefined,
      ...(livro ? { heroDraftId: livro.draftId, heroDraftKey: livro.draftKey } : {}),
    };
    try {
      const resultado =
        metodo === 'pix'
          ? await CompraAvulsaService.gerarPix(base)
          : await CompraAvulsaService.pagarComCartao({
              ...base,
              nomeCartao: nomeCartao.trim(),
              numero: numero.replace(/\D/g, ''),
              cvv,
              mes: Number(mes),
              ano: Number(ano),
            });
      chaveRef.current = novaChave();
      if (resultado.status === 'recusada') {
        setErro('Pagamento não autorizado. Confira os dados ou tente de novo.');
        setTurnstileToken('');
        setTurnstileKey((k) => k + 1);
        return;
      }
      setCompra(resultado);
    } catch (err) {
      setErro(mensagemDeErro(err));
      const reason = reasonDoErro(err);
      if (rascunhoVencido(err)) {
        // 410: o rascunho venceu no Hero Book — o salvo não serve; builder do passo 1.
        limparRascunhoSalvo();
        setAvisoLivro(MSG_RASCUNHO_VENCIDO);
        setLivro(null);
      } else if (reason === 'livro_incompleto' || reason === 'rascunho_invalido') setLivro(null);
      if (reason === 'livro_ja_comprado') {
        limparRascunhoSalvo();
        setLivro(null);
      }
      // Só 400 é resposta definitiva (recusa, validação): a próxima tentativa é
      // outra compra. 504 do nginx/timeout/5xx podem ter cobrado — mantém a
      // chave e o retry devolve a mesma compra em vez de cobrar de novo.
      if (axios.isAxiosError(err) && err.response?.status === 400) chaveRef.current = novaChave();
      setTurnstileToken('');
      setTurnstileKey((k) => k + 1);
    } finally {
      setEnviando(false);
    }
  }

  async function copiarPix(emv: string) {
    try {
      await navigator.clipboard.writeText(emv);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2500);
    } catch {
      setCopiado(false);
    }
  }

  const casca = (conteudo: ReactNode) => (
    <div className="min-h-screen bg-[#f6fafe] flex flex-col font-lato">
      {!inApp && (
        <>
          <div className="lg:hidden flex justify-center">
            <img className="h-12 py-2" src="/logo_clubinho_horizontal_v2.png" alt="Clubinho Corações Preciosos" />
          </div>
          <Header />
        </>
      )}
      <main className="flex-1 w-full flex justify-center px-4 py-6 pb-16">
        <div className="w-full max-w-[560px] flex flex-col gap-4">{conteudo}</div>
      </main>
      {!inApp && <Footer />}
    </div>
  );

  if (erroCarga) {
    return casca(
      <section className="bg-white rounded-2xl border border-[#e6e9f0] p-6 text-center">
        <h1 className="text-xl font-bold text-[#2C2F3A] mb-2">Produto indisponível</h1>
        <p className="text-[#595D69] text-sm">Este link não está disponível no momento.</p>
      </section>,
    );
  }

  if (!produto) {
    return casca(
      <section className="bg-white rounded-2xl border border-[#e6e9f0] p-6 text-center text-[#595D69]">
        Carregando…
      </section>,
    );
  }

  if (compra?.status === 'paga') {
    if (livro) limparRascunhoSalvo();
    return casca(
      <section className="bg-white rounded-2xl border border-[#e6e9f0] p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-[#f6fcf1] border border-[#dae3d2] flex items-center justify-center mx-auto mb-4 text-[#5BB947] text-2xl">
          ✓
        </div>
        <h1 className="text-xl font-bold text-[#2C2F3A] mb-2">Pagamento confirmado!</h1>
        <p className="text-[#595D69] text-sm">
          Recebemos o pagamento do <strong>{compra.produto.nome}</strong> ({formatPreco(compra.valorCentavos)})
          {livro ? (
            <>
              {' '}
              — <strong>{livro.titulo}</strong> com {livro.nomeCrianca} já foi para produção.
            </>
          ) : (
            '. Em breve entraremos em contato para os próximos passos.'
          )}
        </p>
      </section>,
    );
  }

  if (compra && (compra.status === 'expirada' || compra.status === 'estornada')) {
    return casca(
      <section className="bg-white rounded-2xl border border-[#e6e9f0] p-6 text-center">
        <h1 className="text-xl font-bold text-[#2C2F3A] mb-2">
          {compra.status === 'expirada' ? 'O PIX expirou' : 'Pagamento estornado'}
        </h1>
        <button
          type="button"
          onClick={() => setCompra(null)}
          className="mt-4 w-full h-12 rounded-lg bg-[#018ED5] text-white font-semibold"
        >
          Tentar de novo
        </button>
      </section>,
    );
  }

  if (compra?.status === 'pendente' && compra.pix) {
    const pix = compra.pix;
    return casca(
      <section className="bg-white rounded-2xl border border-[#e6e9f0] p-4 lg:p-6 text-center">
        <h1 className="text-xl font-bold text-[#2C2F3A] mb-1">Pague com PIX</h1>
        <p className="text-[#595D69] text-sm mb-4">
          {compra.produto.nome} · {formatPreco(compra.valorCentavos)}
        </p>
        <img src={urlQrCode(pix)} alt="QR Code do PIX" className="w-56 h-56 mx-auto" />
        <p className="text-[#595D69] text-xs mt-4 mb-2">Ou copie o código PIX:</p>
        <div className="rounded-lg border border-[#dadae5] bg-[#F9F9FB] px-3 py-2 text-xs text-[#595D69] break-all text-left">
          {pix.emv}
        </div>
        <button
          type="button"
          onClick={() => copiarPix(pix.emv)}
          className="mt-3 w-full h-12 rounded-lg bg-[#018ED5] text-white font-semibold"
        >
          {copiado ? 'Código copiado!' : 'Copiar código PIX'}
        </button>
        <p className="text-[#A0A3AD] text-xs mt-4">
          Assim que o pagamento cair, esta página confirma sozinha.
          {pix.expiraEm && ` Válido até ${new Date(pix.expiraEm).toLocaleString('pt-BR')}.`}
        </p>
      </section>,
    );
  }

  if (compra?.status === 'pendente') {
    return casca(
      <section className="bg-white rounded-2xl border border-[#e6e9f0] p-6 text-center">
        <h1 className="text-xl font-bold text-[#2C2F3A] mb-2">Pagamento em análise</h1>
        <p className="text-[#595D69] text-sm">
          Sua compra está sendo verificada por segurança. Isso é normal e leva alguns instantes — esta página atualiza
          sozinha.
        </p>
      </section>,
    );
  }

  const campo = 'border border-[#dadae5] w-full px-4 rounded-lg h-12 text-[#2C2F3A]';

  return casca(
    <>
      <section className="bg-white rounded-2xl border border-[#e6e9f0] p-4 lg:p-6">
        <h1 className="text-xl font-bold text-[#2C2F3A] mb-2">{produto.nome}</h1>
        <p className="text-[#595D69] text-sm">Compra exclusiva para assinantes do Clubinho.</p>
        <div className="mt-4 rounded-xl bg-[#DBECFF] px-4 py-3 flex justify-between items-center">
          <span className="text-[#0168AC] font-semibold">
            {livro ? `${livro.titulo} · ${livro.nomeCrianca}` : produto.nome}
          </span>
          <span className="text-[#0168AC] font-bold">{formatPreco(produto.valorCentavos)}</span>
        </div>
        {livro && (
          <button type="button" onClick={() => setLivro(null)} className="mt-2 text-sm font-semibold text-[#018ED5]">
            Editar o livro
          </button>
        )}
      </section>

      {precisaLivro && !livro && (
        <BuilderLivro
          aviso={avisoLivro}
          onPronto={(pronto) => {
            setAvisoLivro(null);
            setErro(null);
            setLivro(pronto);
          }}
        />
      )}

      {(!precisaLivro || livro) && (
        <form onSubmit={enviar} className="bg-white rounded-2xl border border-[#e6e9f0] p-4 lg:p-6 flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-[#595D69]">E-mail da sua assinatura</span>
            <input
              type="email"
              autoComplete="email"
              className={campo}
              placeholder="o mesmo e-mail de acesso ao app"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <PaymentMethodSwitch value={metodo} onChange={setMetodo} />

          {metodo === 'credit-card' && (
            <div className="flex flex-col gap-3">
              <input
                className={campo}
                placeholder="Nome impresso no cartão"
                autoComplete="cc-name"
                value={nomeCartao}
                onChange={(e) => setNomeCartao(e.target.value)}
              />
              <input
                className={campo}
                placeholder="Número do cartão"
                autoComplete="cc-number"
                {...getMaskProps('card')}
                value={numero}
                onChange={(e) => setNumero(applyMask(e.target.value, 'card'))}
              />
              <div className="flex gap-3">
                <input
                  className={campo}
                  placeholder="Validade (MM/AAAA)"
                  autoComplete="cc-exp"
                  {...getMaskProps('validity')}
                  value={validade}
                  onChange={(e) => setValidade(applyMask(e.target.value, 'validity'))}
                />
                <input
                  className={campo}
                  placeholder="CVV"
                  autoComplete="cc-csc"
                  {...getMaskProps('cvv')}
                  value={cvv}
                  onChange={(e) => setCvv(applyMask(e.target.value, 'cvv'))}
                />
              </div>
              <p className="text-xs text-[#A0A3AD]">Pagamento à vista (1x). Cartões de 16 dígitos.</p>
            </div>
          )}

          {metodo === 'pix' && (
            <p className="text-sm text-[#595D69]">
              Ao continuar, geramos um QR Code PIX de {formatPreco(produto.valorCentavos)}.
            </p>
          )}

          {requireTurnstile && (
            <TurnstileWidget
              key={turnstileKey}
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken('')}
              onError={() => setTurnstileToken('')}
            />
          )}

          {erro && <p className="text-sm text-[#D93025] bg-[#FDECEC] rounded-lg px-3 py-2">{erro}</p>}

          <button
            type="submit"
            disabled={!podeEnviar}
            className="w-full h-12 rounded-lg bg-[#5BB947] text-white font-semibold disabled:opacity-50"
          >
            {enviando ? 'Processando…' : metodo === 'pix' ? 'Gerar PIX' : `Pagar ${formatPreco(produto.valorCentavos)}`}
          </button>
        </form>
      )}
    </>,
  );
}
